import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LeadCaptureModal from '../../components/feature/LeadCaptureModal';
import FAQAccordion from './components/FAQAccordion';
import FAQSidebar from './components/FAQSidebar';
import { supabase } from '../../utils/supabase';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { Link } from 'react-router-dom';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  keywords: string[];
  order_position: number;
  views: number;
  helpful_count: number;
}

export default function FAQPage() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const [helpfulIds, setHelpfulIds] = useState<string[]>([]);
  const faqRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Schema.org FAQPage structured data
  useEffect(() => {
    // Meta tags SEO
    document.title = 'Perguntas Frequentes | TG WEB - Sistema para Transportadoras';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Encontre respostas para as dúvidas mais frequentes sobre o TG WEB, sistema de gestão para transportadoras. Gestão de frotas, documentos fiscais, financeiro e mais.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Encontre respostas para as dúvidas mais frequentes sobre o TG WEB, sistema de gestão para transportadoras. Gestão de frotas, documentos fiscais, financeiro e mais.';
      document.head.appendChild(meta);
    }

    if (faqs.length === 0) return;

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.textContent = JSON.stringify(faqSchema);

    // Remove existing script if any
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();

    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('faq-schema');
      if (el) el.remove();
    };
  }, [faqs]);

  // Buscar FAQs do Supabase
  useEffect(() => {
    async function fetchFaqs() {
      try {
        const { data, error } = await supabase
          .from('faqs')
          .select('*')
          .eq('is_published', true)
          .order('order_position', { ascending: true });

        if (error) throw error;
        setFaqs(data || []);
      } catch (error) {
        console.error('Erro ao buscar FAQs:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchFaqs();
  }, []);

  // Extrair categorias únicas
  const categories = useMemo(() => {
    const cats = ['Todos', ...new Set(faqs.map(faq => faq.category))];
    return cats;
  }, [faqs]);

  // Filtrar FAQs
  const filteredFaqs = useMemo(() => {
    return faqs.filter(faq => {
      const matchesCategory = selectedCategory === 'Todos' || faq.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (faq.keywords && faq.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())));
      return matchesCategory && matchesSearch;
    });
  }, [faqs, selectedCategory, searchQuery]);

  // FAQs mais populares
  const popularFaqs = useMemo(() => {
    return [...faqs].sort((a, b) => b.views - a.views).slice(0, 5);
  }, [faqs]);

  // Toggle accordion
  const handleToggle = async (id: string) => {
    const newOpenId = openFaqId === id ? null : id;
    setOpenFaqId(newOpenId);

    // Incrementar views
    if (newOpenId) {
      await supabase
        .from('faqs')
        .update({ views: faqs.find(f => f.id === id)!.views + 1 })
        .eq('id', id);
    }
  };

  // Marcar como útil
  const handleHelpful = async (id: string) => {
    if (helpfulIds.includes(id)) return;

    const faq = faqs.find(f => f.id === id);
    if (!faq) return;

    await supabase
      .from('faqs')
      .update({ helpful_count: faq.helpful_count + 1 })
      .eq('id', id);

    setHelpfulIds([...helpfulIds, id]);
  };

  // Selecionar FAQ da sidebar
  const handleSelectFaq = (id: string) => {
    setOpenFaqId(id);
    setSelectedCategory('Todos');
    setSearchQuery('');
    
    setTimeout(() => {
      faqRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />
      <LeadCaptureModal />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <Breadcrumb
            items={[
              { label: 'Início', href: '/' },
              { label: 'Perguntas Frequentes' },
            ]}
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white mb-6">
              <i className="ri-question-answer-line mr-2"></i>
              Central de Ajuda
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Perguntas Frequentes
            </h1>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Encontre respostas rápidas para suas dúvidas sobre o TG WEB. 
              Se não encontrar o que procura, nossa equipe está pronta para ajudar.
            </p>

            {/* Barra de Busca */}
            <div className="max-w-xl mx-auto relative">
              <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-neutral-400 text-xl"></i>
              </div>
              <input
                type="text"
                placeholder="Buscar pergunta..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-neutral-800 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-secondary shadow-lg text-base"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-4 flex items-center cursor-pointer"
                >
                  <i className="ri-close-line text-neutral-400 hover:text-neutral-600 text-xl"></i>
                </button>
              )}
            </div>

            {/* Contador de resultados */}
            {searchQuery && (
              <p className="mt-4 text-primary-100 text-sm">
                {filteredFaqs.length} {filteredFaqs.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Filtro de Categorias */}
      <section className="py-8 bg-white border-b border-neutral-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-primary text-white shadow-soft'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Lista de FAQs */}
            <div className="lg:col-span-2">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-neutral-200 p-6 animate-pulse">
                      <div className="h-5 bg-neutral-200 rounded w-3/4 mb-4"></div>
                      <div className="h-4 bg-neutral-100 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : filteredFaqs.length === 0 ? (
                <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-neutral-100 rounded-full mx-auto mb-4">
                    <i className="ri-search-line text-3xl text-neutral-400"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                    Nenhuma pergunta encontrada
                  </h3>
                  <p className="text-neutral-500 mb-6">
                    Tente buscar com outras palavras ou entre em contato conosco.
                  </p>
                  <a
                    href="https://wa.me/5511999999999?text=Olá,%20tenho%20uma%20dúvida%20sobre%20o%20TG%20WEB"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>
                    <span>Falar no WhatsApp</span>
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Agrupar por categoria quando não há busca */}
                  {selectedCategory === 'Todos' && !searchQuery ? (
                    categories.filter(c => c !== 'Todos').map((category) => {
                      const categoryFaqs = filteredFaqs.filter(f => f.category === category);
                      if (categoryFaqs.length === 0) return null;

                      return (
                        <div key={category} className="mb-8">
                          <h2 className="text-xl font-bold text-neutral-800 mb-4 flex items-center">
                            <span className="w-2 h-6 bg-secondary rounded-full mr-3"></span>
                            {category}
                          </h2>
                          <div className="space-y-3">
                            {categoryFaqs.map((faq) => (
                              <div key={faq.id} ref={(el) => { faqRefs.current[faq.id] = el; }}>
                                <FAQAccordion
                                  faq={faq}
                                  isOpen={openFaqId === faq.id}
                                  onToggle={() => handleToggle(faq.id)}
                                  onHelpful={handleHelpful}
                                  helpfulIds={helpfulIds}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="space-y-3">
                      {filteredFaqs.map((faq) => (
                        <div key={faq.id} ref={(el) => { faqRefs.current[faq.id] = el; }}>
                          <FAQAccordion
                            faq={faq}
                            isOpen={openFaqId === faq.id}
                            onToggle={() => handleToggle(faq.id)}
                            onHelpful={handleHelpful}
                            helpfulIds={helpfulIds}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-36">
                <FAQSidebar 
                  popularFaqs={popularFaqs} 
                  onSelectFaq={handleSelectFaq}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-primary via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-white rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto para transformar sua transportadora?
            </h2>
            <p className="text-lg text-primary-100 mb-8 max-w-2xl mx-auto">
              Teste o TG WEB gratuitamente por 14 dias e descubra como podemos 
              simplificar sua gestão.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/5511999999999?text=Olá,%20gostaria%20de%20começar%20o%20teste%20gratuito%20do%20TG%20WEB"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-secondary text-white font-semibold rounded-xl hover:bg-secondary-600 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                Começar Teste Gratuito
              </a>
              <a
                href="https://wa.me/5511999999999?text=Olá,%20gostaria%20de%20agendar%20uma%20demonstração%20do%20TG%20WEB"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                Agendar Demonstração
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-neutral-900 mb-8 text-center">
            Explore Mais Recursos
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link
              to="/funcionalidades"
              className="group p-5 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-apps-line text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors">
                Funcionalidades
              </h4>
              <p className="text-sm text-neutral-600">
                Conheça todos os recursos do sistema
              </p>
            </Link>

            <Link
              to="/planos"
              className="group p-5 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-price-tag-3-line text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors">
                Planos e Preços
              </h4>
              <p className="text-sm text-neutral-600">
                Escolha o plano ideal para você
              </p>
            </Link>

            <Link
              to="/sistema-emissao-cte"
              className="group p-5 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-lg mb-3 group-hover:bg-primary/20 transition-colors">
                <i className="ri-file-text-line text-xl text-primary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors">
                Emissor de CT-e
              </h4>
              <p className="text-sm text-neutral-600">
                Guia completo de emissão
              </p>
            </Link>

            <Link
              to="/blog"
              className="group p-5 bg-neutral-50 rounded-xl hover:bg-primary/5 transition-colors border border-neutral-100"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-secondary/10 rounded-lg mb-3 group-hover:bg-secondary/20 transition-colors">
                <i className="ri-article-line text-xl text-secondary"></i>
              </div>
              <h4 className="font-semibold text-neutral-900 mb-1 group-hover:text-primary transition-colors">
                Blog
              </h4>
              <p className="text-sm text-neutral-600">
                Artigos sobre gestão de transportes
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
